import {
  Alert,
  Box,
  Card,
  Dialog,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';
import { FormProvider, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';

import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import useLocales from 'src/hooks/useLocales';
import { useStore } from 'src/stores/store';
import {
  ApproveDiscound,
  IContractInfo,
} from 'src/@types/Choqorak/MainTables/Contract/ContractInfo';
import { useEffect, useMemo } from 'react';
import Iconify from 'src/components/Iconify';
import Label from 'src/components/Label';
import AttachFileIcon from '@mui/icons-material/AttachFile';
interface Props {
  id: number;
}

export default observer(function ContractInfoLock({ id }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();
  const { ContractInfoStore, ShopStore } = useStore();
  const { selectedContractInfoDetail } = ContractInfoStore;
  const { loadShopDetail, selectedShopDetail } = ShopStore;
  const validationSchema = Yup.object().shape({
    // discountAmount: Yup.string(),
  });

  const defaultValues = useMemo<IContractInfo>(
    () => ({
      contractId: selectedContractInfoDetail?.contractId,
      // attachment: selectedContractInfoDetail?.attachment || undefined,
      // contractElectricMeterStartDigree:
      //   selectedContractInfoDetail?.contractElectricMeterStartDigree || undefined,
    }),
    [selectedContractInfoDetail]
  );
  const methods = useForm<any>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { isSubmitting, errors },
  } = methods;

  const onSubmit = (data: IContractInfo) => {
    console.log(data);
    ContractInfoStore.ContractLock(data)
      .then(() => {
        ContractInfoStore.setOpenCloseApprovedAndLockDialog();
        enqueueSnackbar(`${translate('Tostar.UpdateSuccess')}`);
      })
      .catch((err) => {
        var json = JSON.parse(err.request.response);
        if (json.error != null) {
          setError('afterSubmit', { ...err, message: json.message });
        }
      });
  };

  useEffect(() => {
    if (selectedContractInfoDetail) {
      loadShopDetail(selectedContractInfoDetail.shopId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContractInfoDetail?.shopId]);

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3}>
        {/* First column for the form */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(1, 1fr)' },
              }}
            >
              {selectedShopDetail?.electricMeterId !== null ? (
                <RHFTextField
                  name="contractElectricMeterStartDigree"
                  label={translate('contractsInfo.ContractElectricMeterStartDigree')}
                  showAsterisk={true}
                />
              ) : (
                ''
              )}
              <Stack spacing={2} sx={{ mb: 2 }}>
                <>
                  {/* Hidden File Input */}
                  <input
                    type="file"
                    // accept=".jpg"
                    id="contractattachment"
                    onChange={(e) => {
                      const file = e.target.files ? e.target.files[0] : null;
                      setValue('attachment', file);
                    }}
                    style={{ display: 'none' }}
                  />

                  {/* TextField with Attachment Icon */}
                  <TextField
                    label="Attach File"
                    value={watch('attachment')?.name || ''} // Display file name
                    placeholder="No file chosen"
                    InputProps={{
                      readOnly: true, // Prevent manual input
                      startAdornment: (
                        <InputAdornment position="start">
                          {/* Attachment Icon */}
                          <label htmlFor="contractattachment">
                            <IconButton component="span">
                              <AttachFileIcon />
                            </IconButton>
                          </label>
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    size="medium"
                    sx={{ m: 1, width: '100%' }}
                  />
                </>
              </Stack>
              <Stack direction="row" spacing={3}>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  size="small"
                  color="success"
                  loading={isSubmitting}
                  startIcon={<Iconify icon="eva:edit-fill" />}
                >
                  {translate('CRUD.Approve')}
                </LoadingButton>

                <LoadingButton
                  variant="contained"
                  size="small"
                  onClick={() => ContractInfoStore.setOpenCloseApprovedAndLockDialog()}
                  startIcon={<CancelIcon />}
                >
                  {translate('CRUD.Cancle')}
                </LoadingButton>
              </Stack>
            </Box>
          </Card>
        </Grid>

        {/* Second column for displaying year and month information */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* اطلاعات مغازه و مشتری */}
              <Label color="success">
                <Typography variant="body1" align="center">
                  {translate('Shop.ShopInfo')}
                </Typography>
              </Label>

              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>{translate('Customer.CustomerName')}</TableCell>
                    <TableCell>{selectedContractInfoDetail?.customerName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{translate('Shop.ShopCode')}</TableCell>
                    <TableCell>{selectedContractInfoDetail?.shopCode}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{translate('ElectricMeter.ElectricMeterSerial')}</TableCell>
                    <TableCell>{selectedShopDetail?.electricMeterSerial}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
});
